import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  Users,
  UserX,
  AlertCircle,
  TrendingUp,
  Calendar,
  Download,
  Filter,
  CheckCircle,
  XCircle,
  Eye,
  LogIn,
  LogOut,
  Wifi,
  FileText,
  BarChart3
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const AttendancePage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeSection, setActiveSection] = useState('attendance'); // attendance, requests, reports

  // Mock Data
  const [stats, setStats] = useState({
    presentToday: 25,
    absentToday: 3,
    lateToday: 5,
    totalHoursToday: 187.5,
    pendingRequests: 8
  });

  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      // Mock attendance data
      setAttendanceRecords([
        {
          id: 1,
          employee: 'أحمد محمد العتيبي',
          employeeId: 'EMP-001',
          date: '2024-12-05',
          checkIn: '08:05',
          checkOut: '17:10',
          totalHours: 9.08,
          status: 'late',
          location: 'المكتب الرئيسي',
          notes: 'تأخر 5 دقائق'
        },
        {
          id: 2,
          employee: 'فاطمة سعيد الغامدي',
          employeeId: 'EMP-002',
          date: '2024-12-05',
          checkIn: '07:55',
          checkOut: '17:00',
          totalHours: 9.08,
          status: 'present',
          location: 'المكتب الرئيسي',
          notes: ''
        },
        {
          id: 3,
          employee: 'خالد عبدالله الشمري',
          employeeId: 'EMP-003',
          date: '2024-12-05',
          checkIn: '08:00',
          checkOut: '16:30',
          totalHours: 8.5,
          status: 'early_leave',
          location: 'المكتب الرئيسي',
          notes: 'غادر مبكراً بإذن'
        },
        {
          id: 4,
          employee: 'نورة حسن القحطاني',
          employeeId: 'EMP-004',
          date: '2024-12-05',
          checkIn: null,
          checkOut: null,
          totalHours: 0,
          status: 'absent',
          location: '-',
          notes: 'غياب بدون إذن'
        },
        {
          id: 5,
          employee: 'سعود إبراهيم المطيري',
          employeeId: 'EMP-005',
          date: '2024-12-05',
          checkIn: '08:00',
          checkOut: '17:05',
          totalHours: 9.08,
          status: 'present',
          location: 'المكتب الرئيسي',
          notes: ''
        }
      ]);

      // Mock requests data
      setRequests([
        {
          id: 1,
          employee: 'أحمد محمد العتيبي',
          employeeId: 'EMP-001',
          type: 'leave',
          date: '2024-12-06',
          time: '14:00',
          reason: 'موعد طبي',
          status: 'pending',
          submittedDate: '2024-12-05 09:30',
          approver: null,
          notes: ''
        },
        {
          id: 2,
          employee: 'فاطمة سعيد الغامدي',
          employeeId: 'EMP-002',
          type: 'late',
          date: '2024-12-06',
          time: '09:00',
          reason: 'ظروف عائلية',
          status: 'approved',
          submittedDate: '2024-12-05 08:00',
          approver: 'سعود المطيري',
          approvedDate: '2024-12-05 08:15',
          notes: 'معتمد'
        },
        {
          id: 3,
          employee: 'خالد عبدالله الشمري',
          employeeId: 'EMP-003',
          type: 'overtime',
          date: '2024-12-05',
          time: '17:00 - 20:00',
          reason: 'إنهاء مشروع عاجل',
          status: 'approved',
          submittedDate: '2024-12-05 16:00',
          approver: 'سعود المطيري',
          approvedDate: '2024-12-05 16:10',
          notes: 'معتمد'
        },
        {
          id: 4,
          employee: 'نورة حسن القحطاني',
          employeeId: 'EMP-004',
          type: 'break',
          date: '2024-12-05',
          time: '12:00 - 13:00',
          reason: 'استراحة غداء ممتدة',
          status: 'rejected',
          submittedDate: '2024-12-05 11:00',
          approver: 'سعود المطيري',
          approvedDate: '2024-12-05 11:20',
          notes: 'غير معتمد - وقت استراحة عادي كافٍ'
        }
      ]);

      setLoading(false);
    }, 500);
  }, [selectedDate]);

  const getStatusBadge = (status) => {
    const statuses = {
      present: { label: 'حاضر', color: 'bg-green-100 text-green-700 border-green-300' },
      late: { label: 'متأخر', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      absent: { label: 'غائب', color: 'bg-red-100 text-red-700 border-red-300' },
      early_leave: { label: 'خرج مبكراً', color: 'bg-orange-100 text-orange-700 border-orange-300' }
    };
    const statusInfo = statuses[status];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getRequestStatusBadge = (status) => {
    const statuses = {
      pending: { label: 'بانتظار الموافقة', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      approved: { label: 'معتمد', color: 'bg-green-100 text-green-700 border-green-300' },
      rejected: { label: 'مرفوض', color: 'bg-red-100 text-red-700 border-red-300' }
    };
    const statusInfo = statuses[status];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getRequestTypeLabel = (type) => {
    const types = {
      leave: 'استئذان خروج',
      late: 'طلب تأخير',
      overtime: 'عمل إضافي',
      break: 'كسر دوام',
      time_edit: 'تعديل وقت'
    };
    return types[type] || type;
  };

  const filteredRecords = attendanceRecords.filter(record => {
    if (filterStatus === 'all') return true;
    return record.status === filterStatus;
  });

  const handleExport = () => {
    alert('تصدير سجل الحضور إلى Excel');
  };

  const handleViewDetails = (record) => {
    alert(`عرض تفاصيل: ${record.employee}`);
  };

  const handleApproveRequest = (requestId) => {
    alert(`اعتماد الطلب رقم ${requestId}`);
  };

  const handleRejectRequest = (requestId) => {
    alert(`رفض الطلب رقم ${requestId}`);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل بيانات الحضور...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission('hr.attendance.view')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية الوصول لنظام الحضور</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <Clock className="w-8 h-8" />
                <span>نظام الحضور والانصراف</span>
              </h1>
              <p className="text-gray-600">إدارة شاملة لحضور وانصراف الموظفين</p>
            </div>

            <div className="flex items-center gap-3">
              {hasPermission('hr.attendance.report') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#C8A870] text-[#C8A870] rounded-lg font-bold hover:bg-[#C8A870] hover:text-white transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span>تصدير</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards - KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          {/* Present Today */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الحضور اليوم</p>
                <p className="text-3xl font-black text-green-600">{stats.presentToday}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>

          {/* Absent Today */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الغياب اليوم</p>
                <p className="text-3xl font-black text-red-600">{stats.absentToday}</p>
              </div>
              <UserX className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>

          {/* Late Today */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المتأخرون</p>
                <p className="text-3xl font-black text-yellow-600">{stats.lateToday}</p>
              </div>
              <AlertCircle className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>

          {/* Total Hours */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#1E3A8A] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الساعات</p>
                <p className="text-2xl font-black text-[#1E3A8A]">{stats.totalHoursToday}</p>
              </div>
              <Clock className="w-10 h-10 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          {/* Pending Requests */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الطلبات المعلقة</p>
                <p className="text-3xl font-black text-[#C8A870]">{stats.pendingRequests}</p>
              </div>
              <FileText className="w-12 h-12 text-[#C8A870] opacity-20" />
            </div>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 mb-6">
          <div className="flex border-b-2 border-gray-200">
            <button
              onClick={() => setActiveSection('attendance')}
              className={`flex items-center gap-2 px-6 py-4 font-bold transition-all ${
                activeSection === 'attendance'
                  ? 'border-b-4 border-[#1E3A8A] text-[#1E3A8A] bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Clock className="w-5 h-5" />
              سجل الحضور
            </button>
            <button
              onClick={() => setActiveSection('requests')}
              className={`flex items-center gap-2 px-6 py-4 font-bold transition-all ${
                activeSection === 'requests'
                  ? 'border-b-4 border-[#1E3A8A] text-[#1E3A8A] bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-5 h-5" />
              الطلبات ({requests.filter(r => r.status === 'pending').length})
            </button>
            <button
              onClick={() => setActiveSection('reports')}
              className={`flex items-center gap-2 px-6 py-4 font-bold transition-all ${
                activeSection === 'reports'
                  ? 'border-b-4 border-[#1E3A8A] text-[#1E3A8A] bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              التقارير
            </button>
          </div>

          <div className="p-6">
            {/* Attendance Records Section */}
            {activeSection === 'attendance' && (
              <div>
                {/* Filters */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-600" />
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
                    >
                      <option value="all">جميع الحالات</option>
                      <option value="present">حاضر</option>
                      <option value="late">متأخر</option>
                      <option value="absent">غائب</option>
                      <option value="early_leave">خرج مبكراً</option>
                    </select>
                  </div>
                </div>

                {/* Attendance Table */}
                <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الموظف</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">التاريخ</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">وقت الدخول</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">وقت الخروج</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">ساعات العمل</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراء</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredRecords.map((record) => (
                        <tr key={record.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-bold text-gray-800">{record.employee}</p>
                              <p className="text-xs text-gray-500">{record.employeeId}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-sm">{record.date}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <LogIn className="w-4 h-4 text-green-600" />
                              <span className="font-bold text-gray-800">{record.checkIn || '-'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <LogOut className="w-4 h-4 text-red-600" />
                              <span className="font-bold text-gray-800">{record.checkOut || '-'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-bold text-[#1E3A8A]">
                              {record.totalHours > 0 ? `${record.totalHours.toFixed(2)} ساعة` : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">{getStatusBadge(record.status)}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleViewDetails(record)}
                              className="p-2 text-[#1E3A8A] hover:bg-blue-50 rounded-lg transition-colors"
                              title="عرض التفاصيل"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Fingerprint System Info */}
                <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Wifi className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">نظام البصمة</h4>
                      <p className="text-sm text-gray-600">
                        • يتم تسجيل الحضور والانصراف عبر نظام البصمة الإلكترونية
                        <br />
                        • يجب التسجيل من شبكة WiFi المكتب الرئيسي
                        <br />
                        • محاولات التسجيل خارج الشبكة سيتم رفضها تلقائياً
                        <br />
                        • يتم تسجيل جميع المحاولات في النظام
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Requests Section */}
            {activeSection === 'requests' && (
              <div>
                <h3 className="text-lg font-black text-gray-800 mb-4">الطلبات المرتبطة بالحضور</h3>
                
                {requests.length > 0 ? (
                  <div className="space-y-4">
                    {requests.map((request) => (
                      <div
                        key={request.id}
                        className={`border-2 rounded-lg p-4 ${
                          request.status === 'pending'
                            ? 'border-yellow-200 bg-yellow-50'
                            : request.status === 'approved'
                            ? 'border-green-200 bg-green-50'
                            : 'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-bold text-gray-800 text-lg">{getRequestTypeLabel(request.type)}</h4>
                              {getRequestStatusBadge(request.status)}
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                              <span className="font-bold">{request.employee}</span> ({request.employeeId})
                            </p>
                            <p className="text-sm text-gray-600">
                              تاريخ الطلب: {request.submittedDate}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">التاريخ</p>
                            <p className="font-bold text-gray-800">{request.date}</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">الوقت</p>
                            <p className="font-bold text-gray-800">{request.time}</p>
                          </div>
                        </div>

                        <div className="mb-3 p-3 bg-white rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">السبب</p>
                          <p className="text-sm text-gray-800">{request.reason}</p>
                        </div>

                        {request.status !== 'pending' && (
                          <div className="mb-3 p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">
                              {request.status === 'approved' ? 'معتمد من' : 'مرفوض من'}
                            </p>
                            <p className="text-sm font-bold text-gray-800">
                              {request.approver} • {request.approvedDate}
                            </p>
                            {request.notes && (
                              <p className="text-sm text-gray-600 mt-1">ملاحظات: {request.notes}</p>
                            )}
                          </div>
                        )}

                        {request.status === 'pending' && hasPermission('hr.attendance.approve') && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleApproveRequest(request.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
                            >
                              <CheckCircle className="w-4 h-4" />
                              اعتماد
                            </button>
                            <button
                              onClick={() => handleRejectRequest(request.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-all"
                            >
                              <XCircle className="w-4 h-4" />
                              رفض
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>لا توجد طلبات</p>
                  </div>
                )}
              </div>
            )}

            {/* Reports Section */}
            {activeSection === 'reports' && (
              <div>
                <h3 className="text-lg font-black text-gray-800 mb-4">التقارير</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => alert('تقرير ساعات العمل')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
                  >
                    <Clock className="w-8 h-8 text-[#1E3A8A] mb-3" />
                    <h4 className="font-bold text-gray-800 mb-1">تقرير ساعات العمل</h4>
                    <p className="text-sm text-gray-600">عرض ساعات العمل لجميع الموظفين</p>
                  </button>

                  <button
                    onClick={() => alert('تقرير الغياب')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
                  >
                    <UserX className="w-8 h-8 text-red-600 mb-3" />
                    <h4 className="font-bold text-gray-800 mb-1">تقرير الغياب</h4>
                    <p className="text-sm text-gray-600">عرض سجل الغياب والتأخير</p>
                  </button>

                  <button
                    onClick={() => alert('تقرير التأخير')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
                  >
                    <AlertCircle className="w-8 h-8 text-yellow-600 mb-3" />
                    <h4 className="font-bold text-gray-800 mb-1">تقرير التأخير</h4>
                    <p className="text-sm text-gray-600">تحليل حالات التأخير</p>
                  </button>

                  <button
                    onClick={() => alert('تقرير العمل الإضافي')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
                  >
                    <TrendingUp className="w-8 h-8 text-green-600 mb-3" />
                    <h4 className="font-bold text-gray-800 mb-1">تقرير العمل الإضافي</h4>
                    <p className="text-sm text-gray-600">عرض ساعات العمل الإضافية</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AttendancePage;
